import React, { useEffect, type JSX } from "react";
import { Activity, ShieldCheck, Waypoints } from "lucide-react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import type { AdminDashboardDependencies } from "../composition/create-admin-dashboard-dependencies";
import { createAdminShellViewModel } from "./admin-shell-view-model";
import { useAdminDashboardLocale } from "../../shared/locale/admin-dashboard-locale-context";
import { AdminLocalizedNavLink } from "../../shared/locale/admin-dashboard-localized-link";
import { resolveAdminDashboardLocaleFromQuery } from "../../shared/locale/admin-dashboard-locale";
import { withAdminDashboardLocaleTo } from "../../shared/locale/admin-dashboard-locale-routing";

export type AdminShellProps = {
  dependencies: AdminDashboardDependencies;
};

const shellCss = `
:root {
  color: #18212b;
  background:
    radial-gradient(circle at top left, rgba(245, 158, 11, 0.18), transparent 28%),
    radial-gradient(circle at bottom right, rgba(14, 165, 233, 0.12), transparent 24%),
    linear-gradient(180deg, #f7f3ea 0%, #efe6d8 100%);
  font-family: "Segoe UI", sans-serif;
}

* {
  box-sizing: border-box;
}

body {
  margin: 0;
  min-height: 100vh;
}

a {
  color: inherit;
  text-decoration: none;
}

.admin-shell {
  min-height: 100vh;
  display: grid;
  grid-template-columns: 300px minmax(0, 1fr);
}

.admin-shell__sidebar {
  padding: 32px 24px;
  border-right: 1px solid rgba(24, 33, 43, 0.12);
  background: rgba(255, 250, 242, 0.76);
  backdrop-filter: blur(12px);
  display: grid;
  gap: 22px;
  align-content: start;
}

.admin-shell__eyebrow {
  margin: 0;
  color: #9a3412;
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.admin-shell__brand {
  margin: 10px 0 0;
  font-size: 1.5rem;
  font-weight: 700;
  letter-spacing: -0.03em;
}

.admin-shell__nav-group {
  display: grid;
  gap: 10px;
}

.admin-shell__nav-title {
  margin: 0;
  color: #64748b;
  font-size: 0.76rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.admin-shell__nav {
  margin: 0;
  padding: 0;
  list-style: none;
  display: grid;
  gap: 10px;
}

.admin-shell__nav-link {
  display: block;
  padding: 12px 14px;
  border-radius: 14px;
  background: rgba(24, 33, 43, 0.05);
  border: 1px solid transparent;
  font-size: 0.95rem;
  font-weight: 600;
  transition: 150ms ease;
}

.admin-shell__nav-link.active {
  background: rgba(154, 52, 18, 0.12);
  border-color: rgba(154, 52, 18, 0.24);
}

.admin-shell__caption-card {
  padding: 16px;
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.72);
  border: 1px solid rgba(24, 33, 43, 0.08);
  box-shadow: 0 18px 36px rgba(15, 23, 42, 0.06);
}

.admin-shell__caption {
  margin: 10px 0 0;
  color: #475569;
  font-size: 0.92rem;
  line-height: 1.6;
}

.admin-shell__main {
  padding: 32px;
  display: grid;
  gap: 24px;
}

.admin-shell__topbar {
  display: grid;
  gap: 16px;
}

.admin-shell__title-row {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: 12px;
  align-items: center;
}

.admin-shell__title-wrap {
  display: grid;
  gap: 8px;
}

.admin-shell__title {
  margin: 0;
  font-size: clamp(2rem, 4vw, 3.1rem);
  line-height: 1;
  letter-spacing: -0.04em;
}

.admin-shell__subtitle {
  margin: 0;
  max-width: 56rem;
  color: #334155;
  font-size: 1rem;
  line-height: 1.6;
}

.admin-shell__badge-row {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.admin-shell__badge {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  border-radius: 999px;
  background: #fffaf2;
  border: 1px solid rgba(24, 33, 43, 0.1);
  box-shadow: 0 12px 30px rgba(15, 23, 42, 0.06);
  font-weight: 600;
}

.admin-shell__role-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.admin-shell__role-item {
  padding: 8px 12px;
  border-radius: 999px;
  background: rgba(24, 33, 43, 0.06);
  font-size: 0.85rem;
  font-weight: 600;
}

.admin-shell__locale-box {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.76);
  border: 1px solid rgba(24, 33, 43, 0.1);
  box-shadow: 0 12px 30px rgba(15, 23, 42, 0.06);
}

.admin-shell__locale-label {
  color: #64748b;
  font-size: 0.82rem;
  font-weight: 700;
}

.admin-shell__locale-actions {
  display: inline-flex;
  gap: 8px;
}

.admin-shell__locale-button {
  min-height: 32px;
  padding: 0 12px;
  border-radius: 999px;
  border: 1px solid rgba(148, 163, 184, 0.35);
  background: #fff;
  color: #18212b;
  font-size: 0.84rem;
  font-weight: 700;
  cursor: pointer;
}

.admin-shell__locale-button.is-active {
  border-color: rgba(15, 118, 110, 0.3);
  background: rgba(15, 118, 110, 0.12);
  color: #0f766e;
}

.admin-shell__slot {
  min-width: 0;
}

@media (max-width: 960px) {
  .admin-shell {
    grid-template-columns: 1fr;
  }

  .admin-shell__sidebar {
    border-right: 0;
    border-bottom: 1px solid rgba(24, 33, 43, 0.12);
  }

  .admin-shell__main {
    padding: 24px;
  }
}
`;

export function AdminShell(props: AdminShellProps): JSX.Element {
  const { locale, setLocale } = useAdminDashboardLocale();
  const location = useLocation();
  const navigate = useNavigate();
  const viewModel = createAdminShellViewModel(props.dependencies, locale);

  useEffect(() => {
    const queryLocale = resolveAdminDashboardLocaleFromQuery(location.search);
    if (queryLocale && queryLocale !== locale) {
      setLocale(queryLocale);
    }
  }, [locale, location.search, setLocale]);

  useEffect(() => {
    const queryLocale = resolveAdminDashboardLocaleFromQuery(location.search);
    if (queryLocale) {
      return;
    }

    navigate(
      withAdminDashboardLocaleTo(
        {
          pathname: location.pathname,
          search: location.search,
          hash: location.hash,
        },
        locale,
      ),
      { replace: true },
    );
  }, [locale, location.hash, location.pathname, location.search, navigate]);

  function handleLocaleChange(nextLocale: typeof locale): void {
    setLocale(nextLocale);
    navigate(
      withAdminDashboardLocaleTo(
        {
          pathname: location.pathname,
          search: location.search,
          hash: location.hash,
        },
        nextLocale,
      ),
      { replace: true },
    );
  }

  return (
    <>
      <style>{shellCss}</style>
      <div className="admin-shell">
        <aside className="admin-shell__sidebar">
          <div>
            <p className="admin-shell__eyebrow">{viewModel.shellEyebrow}</p>
            <h1 className="admin-shell__brand">{viewModel.shellBrand}</h1>
          </div>

          <nav aria-label="Admin navigation" className="admin-shell__nav-group">
            {viewModel.navigationSections.map((section) => (
              <div className="admin-shell__nav-group" key={section.label}>
                <p className="admin-shell__nav-title">{section.label}</p>
                <ul className="admin-shell__nav">
                  {section.items.map((item) => (
                    <li key={item.to}>
                      <AdminLocalizedNavLink
                        className="admin-shell__nav-link"
                        to={item.to}
                        end={item.to === "/admin"}
                      >
                        {item.label}
                      </AdminLocalizedNavLink>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </nav>

          <section className="admin-shell__caption-card">
            <p className="admin-shell__eyebrow">{viewModel.shellCaptionEyebrow}</p>
            <p className="admin-shell__caption">{viewModel.shellCaption}</p>
          </section>
        </aside>

        <main className="admin-shell__main">
          <header className="admin-shell__topbar">
            <div className="admin-shell__title-row">
              <div className="admin-shell__title-wrap">
                <p className="admin-shell__eyebrow">{viewModel.shellTitleEyebrow}</p>
                <h2 className="admin-shell__title">{viewModel.shellTitle}</h2>
                <p className="admin-shell__subtitle">{viewModel.shellSubtitle}</p>
              </div>

              <div className="admin-shell__badge-row">
                <div className="admin-shell__locale-box">
                  <span className="admin-shell__locale-label">{viewModel.localeLabel}</span>
                  <div className="admin-shell__locale-actions">
                    {viewModel.localeOptions.map((option) => (
                      <button
                        key={option.key}
                        type="button"
                        className={`admin-shell__locale-button${option.key === locale ? " is-active" : ""}`}
                        onClick={() => handleLocaleChange(option.key)}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                </div>
                <span className="admin-shell__badge">
                  <ShieldCheck size={16} />
                  {viewModel.shellBadgeLabels.authMode} {viewModel.authModeLabel}
                </span>
                <span className="admin-shell__badge">
                  <Waypoints size={16} />
                  {viewModel.shellBadgeLabels.environment} {viewModel.environmentName}
                </span>
                <span className="admin-shell__badge">
                  <Activity size={16} />
                  {viewModel.shellBadgeLabels.release} {viewModel.release}
                </span>
                <span className="admin-shell__badge">
                  <Activity size={16} />
                  {viewModel.shellBadgeLabels.bundle} {viewModel.bundleFingerprint}
                </span>
              </div>
            </div>

            <ul className="admin-shell__role-list" aria-label="Admin roles">
              {viewModel.adminRoles.map((role) => (
                <li className="admin-shell__role-item" key={role}>
                  {role}
                </li>
              ))}
            </ul>
          </header>

          <div className="admin-shell__slot">
            <Outlet />
          </div>
        </main>
      </div>
    </>
  );
}
